import React, { use, useEffect, useState } from "react";
import styles from "./GeneralInformationForm.module.css";
import Header from "../../Header/Header";
import { useNavigate } from "react-router-dom";
import config from "../../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Balls from "../../Templates/Loading";
import { Spin } from "antd";
import ProgressBar from "../../../components/ProgressBar";
import { validateEmail } from "../../../helpers/Helper";
import PhoneInput from "../../../components/PhoneInput/PhoneInput";
import { useProfile } from "../../../contexts/ProfileContext";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { validateUSPhoneNumber, showErrorToast, showSuccessToast } from "../../utils";

const GeneralInformationForm = () => {
    const navigate = useNavigate();
    const { profileData: profile } = useProfile();

    // State variables to capture form input
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [email, setEmail] = useState("");
    const [phoneError, setPhoneError] = useState(null);
    const [btnLoading, setBtnLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Storage key for persisting form data
    const STORAGE_KEY = "getQualified_generalInfo";

    // Load form data from storage or profile on component mount
    useEffect(() => {
        const savedData = localStorage.getItem(STORAGE_KEY);

        if (savedData) {
            // Use saved form data if available
            try {
                const parsedData = JSON.parse(savedData);
                setFirstName(parsedData.firstName || "");
                setLastName(parsedData.lastName || "");
                setPhoneNumber(parsedData.phoneNumber || "1");
                setEmail(parsedData.email || "");
            } catch (error) {
                console.error("Error parsing saved form data:", error);
                // Fall back to profile data if saved data is corrupted
                initializeFromProfile();
            }
        } else {
            // Initialize from profile if no saved data
            initializeFromProfile();
        }
    }, [profile]);

    const initializeFromProfile = () => {
        setFirstName(profile?.first_name || "");
        setLastName(profile?.last_name || "");

        // Ensure phone number is properly formatted for US
        if (profile?.phone) {
            const digitsOnly = profile.phone.replace(/\D/g, "");
            if (digitsOnly.length === 10) {
                // Add US country code if it's a 10-digit number
                setPhoneNumber("1" + digitsOnly);
            } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
                // Already has US country code
                setPhoneNumber(digitsOnly);
            } else {
                setPhoneNumber(profile.phone);
            }
        } else {
            setPhoneNumber("1");
        }

        setEmail(profile?.email || "");
    };

    // Save form data to localStorage whenever form fields change
    useEffect(() => {
        const formData = {
            firstName,
            lastName,
            phoneNumber,
            email,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }, [firstName, lastName, phoneNumber, email]);

    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevent the default form submission behavior

        // Validate form inputs
        if (!validateForm()) return;
        setBtnLoading(true);

        const authUserToken = localStorage.getItem("authUser");
        const authUser = authUserToken ? authUserToken.replace(/^"|"$/g, "") : "";

        const payload = {
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone_number: phoneNumber,
        };
        navigate("/get-qualified/Step-2", { state: { payload } });
        try {
            // const response = await fetch(`${config.get("API_URL")}/personal_info`, {
            //     method: "POST",
            //     headers: {
            //         "Content-Type": "application/json",
            //         Authorization: `Bearer ${authUser}`,
            //     },
            //     body: JSON.stringify({
            //         first_name: firstName,
            //         last_name: lastName,
            //         email: email,
            //         phone_number: phoneNumber,
            //         // address: address,
            //         // unit_number: unitNumber,
            //     }),
            // });

            // const responseData = await response.json(); // Parse the response body

            // if (response.ok) {
            //     navigate("/get-qualified/Step-2");
            // } else {
            //     console.log("Error Response:", responseData); // Log the error response
            //     showErrorToast(`Failed to submit: ${responseData.message || "Please try again."}`, {
            //         autoClose: 3000,
            //         pauseOnHover: false,
            //     });
            // }
            // navigate("/get-qualified/Step-2");
            setBtnLoading(false);
        } catch (error) {
            console.error("Error submitting the form:", error);
            showErrorToast("An error occurred. Please try again.", {
                autoClose: 3000,
                pauseOnHover: false,
            });
        }
    };

    useEffect(() => {
        document.title = `Financing | ${config.get("COMPANY_NAME")}`;
    }, []);

    const validateForm = () => {
        if (!firstName || !lastName || !phoneNumber || !email) {
            showErrorToast("Please fill in all fields.", {
                autoClose: 3000,
                pauseOnHover: false,
            });
            return false;
        }

        if (!validateUSPhoneNumber(phoneNumber)) {
            showErrorToast("Please enter a valid US phone number.", {
                autoClose: 3000,
                pauseOnHover: false,
            });
            return false;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            showErrorToast("Please enter a valid email address.", {
                autoClose: 3000,
                pauseOnHover: false,
            });
            return false;
        }

        return true; // If everything is valid
    };

    return (
        <div className={`${styles.container} tw-h-screen max-sm:tw-w-screen  tw-bg-background_color`}>
            <main className={`${styles.mainContent} tw-overflow-y-auto`}>
                <Header title="Apply for financing" />
                <section className={`${styles.formSection} max-sm:tw-py-[30px] tw-pt-[30px] tw-items-center `}>
                    <ProgressBar currentStep={1} totalSteps={6} />
                    <div className={`${styles.formContainer} tw-w-full tw-mt-10   `}>
                        <h1 className={`${styles.formTitle} `}>General Information</h1>
                        <p className={`${styles.formDescription} tw-text-justify    `}>
                            We'll take care of the paperwork so you can sit back and relax. Rest assured, your info is safe with us and won't be
                            shared with anyone without your say-so, for more clarity{" "}
                            <a onClick={() => navigate("/privacy-policy")} className={styles.privacyLink}>
                                learn more about our privacy Policy
                            </a>
                        </p>
                        <form className={styles.generalForm} onSubmit={handleSubmit}>
                            <div className={styles.formRow}>
                                <div className={styles.fieldContainer}>
                                    <label className={styles.fieldLabel}>First Name</label>
                                    <input
                                        type="text"
                                        required
                                        className={styles.fieldInput}
                                        placeholder="Enter your First name"
                                        value={firstName}
                                        maxLength={50}
                                        onChange={(e) => {
                                            if (e.target.value.length > 50) {
                                                e.target.value = e.target.value.slice(0, 50);
                                                setFirstName(e.target.value);
                                            } else {
                                                setFirstName(e.target.value);
                                            }
                                        }}
                                    />
                                </div>
                                <div className={styles.fieldContainer}>
                                    <label className={styles.fieldLabel}>Last Name</label>
                                    <input
                                        type="text"
                                        required
                                        className={styles.fieldInput}
                                        placeholder="Enter your Last name"
                                        value={lastName}
                                        maxLength={50}
                                        onChange={(e) => {
                                            if (e.target.value.length > 50) {
                                                e.target.value = e.target.value.slice(0, 50);
                                                setLastName(e.target.value);
                                            } else {
                                                setLastName(e.target.value);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={styles.formRow}>
                                <div className={styles.fieldContainer}>
                                    <label className={styles.fieldLabel}>Phone Number</label>
                                    <PhoneInput phone={phoneNumber} setPhone={setPhoneNumber} />

                                    {phoneError && <p className={"tw-text-red-500"}>{phoneError}</p>}
                                </div>
                                <div className={styles.fieldContainer}>
                                    <label className={styles.fieldLabel}>Email Address</label>
                                    <input
                                        type="email"
                                        required
                                        className={styles.fieldInput}
                                        placeholder="Enter your Email Address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onBlur={(e) =>
                                            e.target.value && validateEmail(e.target.value)
                                                ? setErrors((prev) => ({ ...prev, email: "" }))
                                                : setErrors((prev) => ({ ...prev, email: "Invalid email address" }))
                                        }
                                    />
                                    {errors?.email && <p className="tw-text-red-500 ">{errors.email}</p>}
                                </div>
                            </div>

                            <p className="tw-text-secondary tw-italic tw-text-[12px] tw-text-justify">
                                Your privacy is our priority. We'll never share your information without your explicit consent
                            </p>
                            <div className="tw-w-full tw-flex tw-justify-center tw-mt-8">
                                <button
                                    type="submit"
                                    className={`${styles.submitButton} tw-bg-primary hover:tw-bg-primary_hover tw-w-fit tw-px-[40px]`}
                                >
                                    {btnLoading ? <Spin /> : "Next"}
                                </button>
                            </div>
                        </form>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default GeneralInformationForm;
